import { fetchWithAuth } from './apiUtils';
import { getAuthToken } from './authService';
import { API_URL } from '../constants/api';

const API_BASE_URL = `${API_URL}/distributors`;

/**
 * Servicio para gestión de distribuidores
 */
const distributorService = {
  /**
   * Obtener todos los distribuidores con paginación
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 1)
   * @param {number} options.limit - Elementos por página (default: 10)
   * @param {string} options.search - Término de búsqueda
   * @param {boolean} options.activeOnly - Filtrar solo distribuidores activos
   * @returns {Promise} - Promesa con la lista de distribuidores paginada
   */
  async getAllDistributors(options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.search) queryParams.append('search', options.search);
    if (options.activeOnly !== undefined) queryParams.append('activeOnly', options.activeOnly);
    
    const url = queryParams.toString() ? `${API_BASE_URL}?${queryParams}` : API_BASE_URL;
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Obtener estadísticas de distribuidores
   * @returns {Promise} - Promesa con las estadísticas
   */
  async getDistributorStats() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/stats`, {}, token);
  },

  /**
   * Obtener distribuidores activos para selección
   * @returns {Promise} - Promesa con la lista de distribuidores activos
   */
  async getActiveDistributors() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/active`, {}, token);
  },

  /**
   * Obtener un distribuidor por ID
   * @param {string} id - ID del distribuidor
   * @returns {Promise} - Promesa con el distribuidor
   */
  async getDistributorById(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {}, token);
  },

  /**
   * Crear un nuevo distribuidor
   * @param {Object} distributorData - Datos del distribuidor
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async createDistributor(distributorData) {
    const token = getAuthToken();
    return await fetchWithAuth(API_BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(distributorData),
    }, token);
  },

  /**
   * Actualizar un distribuidor
   * @param {string} id - ID del distribuidor
   * @param {Object} distributorData - Datos actualizados del distribuidor
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateDistributor(id, distributorData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(distributorData),
    }, token);
  },

  /**
   * Eliminar un distribuidor
   * @param {string} id - ID del distribuidor
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async deleteDistributor(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
    }, token);
  },

  /**
   * Activar/desactivar distribuidor
   * @param {string} id - ID del distribuidor
   * @param {boolean} isActive - Estado activo/inactivo
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async toggleDistributorStatus(id, isActive) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: isActive }),
    }, token);
  },

  /**
   * Obtener dashboard completo de un distribuidor
   * @param {string} id - ID del distribuidor
   * @param {Object} options - Opciones de consulta
   * @param {number} options.contactsPage - Página de contactos (default: 1)
   * @param {number} options.contactsLimit - Límite de contactos por página (default: 10)
   * @returns {Promise} - Promesa con el dashboard del distribuidor
   */
  async getDistributorDashboard(id, options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.contactsPage) queryParams.append('contactsPage', options.contactsPage);
    if (options.contactsLimit) queryParams.append('contactsLimit', options.contactsLimit);
    
    const url = queryParams.toString() 
      ? `${API_BASE_URL}/${id}/dashboard?${queryParams}` 
      : `${API_BASE_URL}/${id}/dashboard`;
    
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Obtener contactos de un distribuidor específico
   * @param {string} id - ID del distribuidor
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 1)
   * @param {number} options.limit - Elementos por página (default: 10)
   * @param {boolean} options.withSubscription - Filtrar por suscripción
   * @returns {Promise} - Promesa con los contactos del distribuidor
   */
  async getDistributorContacts(id, options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.withSubscription !== undefined) queryParams.append('withSubscription', options.withSubscription);
    
    const url = queryParams.toString() 
      ? `${API_BASE_URL}/${id}/contacts?${queryParams}` 
      : `${API_BASE_URL}/${id}/contacts`;
    
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Buscar distribuidor por cuenta eWeLink
   * @param {string} cuenta - Cuenta eWeLink
   * @returns {Promise} - Promesa con el distribuidor encontrado
   */
  async findDistributorByCuentaEwelink(cuenta) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/cuenta/${encodeURIComponent(cuenta)}`, {}, token);
  },
};

/**
 * Buscar distribuidores paginados por nombre de empresa, distribuidor, celular o cuenta eWeLink
 * @param {string} search - Término de búsqueda
 * @param {number} page - Número de página (1-based)
 * @param {number} limit - Resultados por página
 * @param {boolean} activeOnly - Solo distribuidores activos
 * @returns {Promise<{distributors: Array, pagination: Object}>}
 */
export async function searchDistributorsPaginated(search, page = 1, limit = 10, activeOnly = false) {
  const token = getAuthToken();
  
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('page', page);
  params.append('limit', limit);
  if (activeOnly) params.append('activeOnly', activeOnly);

  const url = `${API_BASE_URL}?${params.toString()}`;
  const response = await fetchWithAuth(url, {}, token);

  if (!response.success) {
    throw new Error(response.message || 'Error buscando distribuidores');
  }

  // El backend retorna { data: { distributors, pagination }, ... }
  return {
    distributors: response.data.distributors,
    pagination: response.data.pagination
  };
}

export default distributorService;